import { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  loadUserProfile,
  setCachedUserProfile,
} from '../services/userProfileService';
import { invalidateProfileCache } from '../services/profileCache';

const authorProfileCache = new Map();

export const getNewsAuthorCacheKey = (authorId, authorName) => {
  if (authorId) return `id:${authorId}`;
  if (authorName) return `name:${String(authorName).trim()}`;
  return '';
};

const loadAuthorProfile = async (authorId, authorName) => {
  if (authorId) {
    const profile = await loadUserProfile(authorId);
    if (profile) {
      return profile;
    }
  }

  if (!authorName) return null;

  const snapshot = await getDocs(query(
    collection(db, 'users'),
    where('username', '==', authorName),
    limit(1)
  ));

  if (snapshot.empty) {
    return null;
  }

  const profile = {
    id: snapshot.docs[0].id,
    ...snapshot.docs[0].data(),
  };
  setCachedUserProfile(profile.id, profile);
  return profile;
};

export const invalidateAuthorProfileCache = (uid) => {
  if (!uid) {
    authorProfileCache.clear();
    invalidateProfileCache();
    return;
  }

  invalidateProfileCache(uid);

  for (const [key, value] of authorProfileCache.entries()) {
    if (key === `id:${uid}` || value?.id === uid) {
      authorProfileCache.delete(key);
    }
  }
};

const useNewsAuthor = (news) => {
  const authorId = news?.authorId || news?.authorUid || null;
  const authorName = news?.author || null;
  const cacheKey = getNewsAuthorCacheKey(authorId, authorName);
  const [authorProfile, setAuthorProfile] = useState(() => {
    const cached = authorProfileCache.get(cacheKey);
    return cached && typeof cached.then !== 'function' ? cached : null;
  });

  useEffect(() => {
    let active = true;

    if (!cacheKey) {
      setAuthorProfile(null);
      return undefined;
    }

    setAuthorProfile(null);

    if (authorProfileCache.has(cacheKey)) {
      const cached = authorProfileCache.get(cacheKey);
      Promise.resolve(cached).then((profile) => {
        if (active) setAuthorProfile(profile);
      });
      return () => {
        active = false;
      };
    }

    const request = loadAuthorProfile(authorId, authorName)
      .then((profile) => {
        authorProfileCache.set(cacheKey, profile);
        return profile;
      })
      .catch((error) => {
        authorProfileCache.delete(cacheKey);
        throw error;
      });

    authorProfileCache.set(cacheKey, request);

    request
      .then((profile) => {
        if (active) setAuthorProfile(profile);
      })
      .catch((error) => {
        console.error('Помилка при пошуку автора:', error);
        if (active) setAuthorProfile(null);
      });

    return () => {
      active = false;
    };
  }, [authorId, authorName, cacheKey]);

  return authorProfile;
};

export default useNewsAuthor;
